import { useState } from 'react';
import Iconify from 'src/components/Iconify';
import { TableMoreMenu } from 'src/components/table';
import RelatedPersonAction from './RelatedPersonAction';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { Chip, MenuItem, TableCell, TableRow, Typography } from '@mui/material';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { isEmpty } from 'lodash';
import uuidv4 from 'src/utils/uuidv4';
import { Delete } from '@mui/icons-material';

type Props = {
  row: any;
  patient: WrappedPatient | null;
  handleRelatedPerson: (data: any) => Promise<any>;
  relationshipValueSet: WrappedValueSet | null;
};

export default function RelatedPersonRow({
  row,
  patient,
  handleRelatedPerson,
  relationshipValueSet,
}: Props) {

  const { i18n } = useLocales();

  const [data, setData] = useState(null);
  const [openMenu, setOpenMenuActions] = useState<HTMLElement | null>(null);
  const [openModalEdit, setOpenModalEdit] = useState(false);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setOpenMenuActions(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setOpenMenuActions(null);
  };

  const handleEdit = (row: any) => {
    handleCloseMenu();
    setData(row);
    setOpenModalEdit(true);
  };

  const handleDelete = (row: any) => {
    handleCloseMenu();
    handleRelatedPerson({
      ...row,
      active: false
    });
  };

  return (
    <>
      <TableRow>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          {row?.getFullName()}
        </TableCell>
        <TableCell style={{ cursor: 'pointer' }} sx={{ fontSize: `0.75rem` }}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="caption">
              {row?.telecom?.filter((e: any) => e.system === 'phone')?.[0]?.value}
            </Typography>
            <Typography variant="caption">
              {row?.telecom?.filter((e: any) => e.system === 'email')?.[0]?.value}
            </Typography>
          </div>
        </TableCell>
        <TableCell >
          {!isEmpty(row?.relationship) &&
            row?.relationship?.map(({ text }) => (
              <Chip
                clickable={false}
                size="small"
                key={uuidv4()}
                label={text}
                sx={{ m: 0.2, textTransform: 'capitalize' }}
              />
          ))}
        </TableCell>
        <TableCell align="center">
          <TableMoreMenu
            open={openMenu}
            onOpen={handleOpenMenu}
            onClose={handleCloseMenu}
            actions={
              <>
                {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.EDIT] }) && 
                  <MenuItem key={'menu-i-edit-' + row?.id} onClick={() => handleEdit(row)}>
                    <Iconify icon={'mdi:pencil'} />
                    Edit
                  </MenuItem>
                }
                {checkAclValidation({ acls: [crsAcls.CRS.PATIENT.RELATED_PERSONS.DELETE] }) && 
                  <MenuItem
                    disabled={!row?.active}
                    key={'menu-i-delete-' + row?.id} 
                    onClick={() => handleDelete(row)}
                  >
                    <Delete color='error'/>
                    Delete
                  </MenuItem>
                }    
              </>
            }
          />
        </TableCell>
      </TableRow>
      <RelatedPersonAction
        title={i18n('patients.details.relatedPersons.titleEditRelatedPersons', 'crs')}
        patient={patient}
        relatedPerson={data}
        open={openModalEdit}
        onClose={() => setOpenModalEdit(false)}
        handleRelatedPerson={handleRelatedPerson}
        relationshipValueSet={relationshipValueSet}
      />
    </>
  );
}
